<template>
  <div class="payments-wrapper">
    <header class="page-header fade-in">
      <div>
        <h1>Paiements du vendeur</h1>
        <p>Consultez les paiements liés à vos ventes.</p>
      </div>
      <div class="header-actions">
        <button class="btn btn-outline" @click="refresh" :disabled="loading">
          <i class="bi bi-arrow-clockwise"></i>
          <span>Actualiser</span>
        </button>
      </div>
    </header>

    <section class="page-body">
      <div class="filters">
        <div class="search">
          <i class="bi bi-search"></i>
          <input
            v-model="searchTerm"
            type="search"
            placeholder="Rechercher (référence, client...)"
          />
        </div>
        <div class="stats" v-if="filteredPayments.length">
          <span>{{ filteredPayments.length }} paiement(s)</span>
        </div>
      </div>

      <div v-if="loading" class="state">
        <div class="loader"></div>
        <p>Chargement des paiements…</p>
      </div>

      <div v-else-if="!filteredPayments.length" class="state">
        <i class="bi bi-credit-card"></i>
        <h3>Aucun paiement</h3>
        <p>Aucun paiement trouvé pour vos ventes.</p>
      </div>

      <div v-else class="payments-grid">
        <article
          v-for="payment in filteredPayments"
          :key="payment.reference || payment.id"
          class="payment-card fade-in"
        >
          <div class="card-top">
            <div class="identity">
              <div class="identity-row">
                <h3>{{ payment.reference || payment.payment_reference }}</h3>
              </div>
              <p class="identity-sub" v-if="payment.customer">
                Client:
                {{
                  payment.customer.fullName ||
                  payment.customer.full_name ||
                  payment.customer.reference ||
                  "—"
                }}
              </p>
              <p class="identity-sub" v-if="payment.user">
                Utilisateur:
                {{ payment.user.name || payment.user.reference || "—" }}
              </p>
            </div>
            <button
              class="btn btn-ghost"
              @click="
                viewPayment(payment.reference || payment.payment_reference)
              "
            >
              <i class="bi bi-arrow-up-right"></i>
            </button>
          </div>

          <div class="card-details">
            <div class="detail">
              <span class="label"
                ><i class="bi bi-currency-exchange"></i>Montant</span
              >
              <span class="value">{{ formatCurrency(payment.amount) }}</span>
            </div>
            <div class="detail">
              <span class="label"><i class="bi bi-calendar-date"></i>Date</span>
              <span class="value">{{
                formatDate(
                  payment.date || payment.payment_date || payment.created_at
                )
              }}</span>
            </div>
            <div class="detail" v-if="payment.remark">
              <span class="label"
                ><i class="bi bi-chat-left-text"></i>Remarque</span
              >
              <span class="value">{{ payment.remark }}</span>
            </div>
          </div>

          <div class="card-footer">
            <div class="actions">
              <button
                class="btn btn-outline"
                @click="
                  viewPayment(payment.reference || payment.payment_reference)
                "
              >
                <i class="bi bi-eye"></i>
                <span>Voir détails</span>
              </button>
            </div>
          </div>
        </article>
      </div>
    </section>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "SellerPaymentsList",
  data() {
    return {
      loading: false,
      searchTerm: "",
    };
  },
  computed: {
    ...mapGetters("sellerPayment", {
      payments: "getAll",
    }),
    filteredPayments() {
      if (!this.searchTerm) {
        return this.payments || [];
      }
      const term = this.searchTerm.toLowerCase();
      return (this.payments || []).filter((payment) => {
        const customerName = payment.customer
          ? payment.customer.fullName ||
            payment.customer.full_name ||
            payment.customer.reference ||
            ""
          : "";
        const userName = payment.user
          ? payment.user.name || payment.user.reference || ""
          : "";
        const values = [
          payment.reference,
          payment.payment_reference,
          customerName,
          userName,
          payment.remark,
        ]
          .filter(Boolean)
          .map((value) => value.toString().toLowerCase());
        return values.some((value) => value.includes(term));
      });
    },
  },
  methods: {
    formatCurrency(amount) {
      if (!amount && amount !== 0) return "—";
      const number = Number(amount);
      if (Number.isNaN(number)) {
        return amount;
      }
      return number.toLocaleString("fr-FR", {
        style: "currency",
        currency: "MAD",
      });
    },
    formatDate(value) {
      if (!value) return "—";
      try {
        return new Date(value).toLocaleDateString("fr-FR", {
          year: "numeric",
          month: "long",
          day: "numeric",
        });
      } catch (error) {
        return value;
      }
    },
    async fetchPayments() {
      this.loading = true;
      try {
        await this.$store.dispatch("sellerPayment/getSellerPayments");
      } catch (error) {
        console.error("Error loading payments", error);
        this.$toast.error("Impossible de charger les paiements.");
      } finally {
        this.loading = false;
      }
    },
    refresh() {
      this.fetchPayments();
    },
    viewPayment(reference) {
      if (!reference) return;
      this.$router.push({ name: "payments-show", params: { reference } });
    },
  },
  mounted() {
    this.fetchPayments();
  },
};
</script>

<style scoped>
.payments-wrapper {
  min-height: 100vh;
  background: linear-gradient(180deg, #f7f8fb 0%, #f0f2f8 100%);
  padding: 2rem 2.5rem 4rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1.5rem;
}

.page-header h1 {
  margin: 0 0 0.5rem;
  font-size: 2.25rem;
  font-weight: 700;
  color: #0f172a;
}

.page-header p {
  margin: 0;
  color: #556073;
  max-width: 480px;
}

.header-actions {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.page-body {
  background: #ffffff;
  border-radius: 24px;
  padding: 2rem;
  box-shadow: 0 24px 45px rgba(15, 23, 42, 0.08);
  display: flex;
  flex-direction: column;
  gap: 1.75rem;
}

.filters {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
}

.search {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.85rem 1.2rem;
  border-radius: 16px;
  background: #f3f4f8;
  border: 1px solid #e4e7f1;
  min-width: 280px;
  flex: 1;
}

.search input {
  border: none;
  background: transparent;
  outline: none;
  font-size: 1rem;
  width: 100%;
}

.stats {
  color: #6b7280;
  font-weight: 600;
}

.state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 4rem 1rem;
  color: #6b7280;
  text-align: center;
}

.state i {
  font-size: 3rem;
  color: #2563eb;
}

.loader {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 4px solid #d1d5db;
  border-top-color: #2563eb;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.payments-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 1.5rem;
}

.payment-card {
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.98), #ffffff 100%);
  border-radius: 20px;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  box-shadow: 0 18px 32px rgba(15, 23, 42, 0.08);
  border: 1px solid rgba(148, 163, 184, 0.12);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.payment-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 24px 46px rgba(15, 23, 42, 0.12);
}

.card-top {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 1rem;
  align-items: start;
}

.identity {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.identity-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.identity-row h3 {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 600;
  color: #0f172a;
}

.identity-sub {
  margin: 0;
  color: #64748b;
  font-size: 0.95rem;
}

.btn.btn-ghost {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  border: none;
  background: rgba(37, 99, 235, 0.08);
  color: #1d4ed8;
  font-size: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;
}

.btn.btn-ghost:hover {
  background: rgba(37, 99, 235, 0.2);
}

.card-details {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 1rem;
}

.detail {
  background: #f8faff;
  border-radius: 16px;
  padding: 0.85rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6b7280;
}

.label i {
  color: #2563eb;
}

.value {
  color: #0f172a;
  font-weight: 600;
  word-break: break-word;
}

.card-footer {
  margin-top: auto;
}

.actions {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.1rem;
  border-radius: 14px;
  font-weight: 600;
  font-size: 0.95rem;
  border: none;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.btn i {
  font-size: 1.1rem;
}

.btn:hover {
  transform: translateY(-2px);
}

.btn-primary {
  background: linear-gradient(135deg, #2563eb, #4f46e5);
  color: white;
  box-shadow: 0 10px 22px rgba(79, 70, 229, 0.25);
}

.btn-outline {
  border: 1px solid rgba(148, 163, 184, 0.6);
  background: white;
  color: #475569;
}

.btn-outline:hover {
  border-color: #4f46e5;
  color: #4f46e5;
}

@media (max-width: 1024px) {
  .payments-wrapper {
    padding: 1.5rem 1.5rem 4rem;
  }
  .page-header {
    flex-direction: column;
  }
  .header-actions {
    width: 100%;
    justify-content: flex-start;
  }
}

@media (max-width: 768px) {
  .payments-wrapper {
    padding: 1.25rem 1rem 4rem;
  }
  .page-body {
    padding: 1.5rem;
  }
  .search {
    min-width: unset;
  }
  .payments-grid {
    grid-template-columns: 1fr;
  }
  .card-details {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .page-header h1 {
    font-size: 1.75rem;
  }
  .page-body {
    padding: 1.25rem;
  }
  .payment-card {
    padding: 1.25rem;
  }
  .btn {
    width: 100%;
    justify-content: center;
  }
  .actions {
    flex-direction: column;
  }
}
</style>
